/**
 * useCenterPanelSubscription — listens for agent-driven center panel render events.
 *
 * The agent calls `render_center_panel` which publishes an AppSync event on
 * the 'centerPanel' channel with a RENDER event type. The payload contains
 * the layout and component specs for the dynamic center panel.
 *
 * Usage:
 *   const { panelConfig, clearPanel } = useCenterPanelSubscription(sessionId);
 *   // panelConfig: { layout, components } or null
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useChannelSubscription } from './useAppSyncSubscription';
import { getCenterPanel } from '../api/chat';

export function useCenterPanelSubscription(sessionId) {
  const [panelConfig, setPanelConfig] = useState(null);
  const [modalConfig, setModalConfig] = useState(null);
  // Avoid hydration storms — debounce/rate-limit re-fetches triggered by
  // chat-message arrivals.
  const hydrationInFlightRef = useRef(false);
  const lastHydrationAtRef = useRef(0);
  // When the user navigates away from an agent-driven page, AppShell calls
  // clearPanel() — but the persisted server-side snapshot remains, and the
  // next chat MESSAGE would re-hydrate it, ignoring the user's nav. This ref
  // suppresses hydration until the agent pushes a fresh live RENDER event
  // (which is an explicit signal the agent wants to draw on the current page)
  // or the chat session is explicitly switched.
  const suppressHydrationRef = useRef(false);
  // Tracks the previous sessionId so the session-change effect can
  // distinguish initial establishment (null → real id) from a real switch
  // (real id → different real id). Only the latter should lift suppression.
  const prevSessionIdRef = useRef(null);

  const handleEvent = useCallback((event) => {
    if (!event) return;

    const { eventType, parsedPayload } = event;

    if (eventType === 'RENDER') {
      try {
        // parsedPayload is already parsed by useChannelSubscription
        const config = parsedPayload || {};
        console.log('Center panel render config:', config);
        // A live RENDER means the agent is intentionally drawing on the
        // current view — re-enable hydration so future chat events can
        // self-heal a missed subscription.
        suppressHydrationRef.current = false;
        if (config.layout === 'modal') {
          // Modal overlays the main panel rather than replacing it.
          setModalConfig(config);
        } else {
          setPanelConfig(config);
        }
      } catch (err) {
        console.error('Failed to process center panel config:', err);
      }
    } else if (eventType === 'CLEAR') {
      setPanelConfig(null);
      setModalConfig(null);
    }
  }, []);

  useChannelSubscription(sessionId, 'centerPanel', handleEvent);

  // Hydrate from the persisted server-side snapshot. Used both on session
  // change AND whenever a chat MESSAGE arrives (so we recover from the
  // AppSync subscription establishment race — the first render event is
  // sometimes published before the centerPanel subscription is fully
  // connected, and is silently dropped). The chat subscription tends to be
  // up sooner since it carries the user's own message; piggybacking on it
  // gives us a reliable signal that "the agent has been doing something
  // recently — go check S3 for any panel updates we missed."
  const hydrate = useCallback(async (sid) => {
    if (!sid) return;
    // After the user navigates away from an agent-driven page, ignore
    // hydration until the agent pushes a fresh RENDER for the current view.
    if (suppressHydrationRef.current) return;
    if (hydrationInFlightRef.current) return;
    // Throttle: at most one hydration call per ~1s. Adequate for chat-event
    // bursts and well below any cost concern.
    const now = Date.now();
    if (now - lastHydrationAtRef.current < 1000) return;
    hydrationInFlightRef.current = true;
    lastHydrationAtRef.current = now;
    try {
      const result = await getCenterPanel(sid);
      const payload = result && result.payload;
      if (payload && payload.layout && Array.isArray(payload.components)) {
        if (payload.layout !== 'modal') {
          setPanelConfig(payload);
        }
      }
    } catch (err) {
      // No persisted panel or endpoint error — subscription will deliver
      // any live renders. Silent here.
      console.debug('Center panel hydration returned nothing:', err && err.message);
    } finally {
      hydrationInFlightRef.current = false;
    }
  }, []);

  // Hydrate on session change. Only lift suppression on a real session
  // switch (real id → different real id) — not on initial establishment
  // (null → real id), which fires on every page mount and would otherwise
  // restore a stale persisted panel after the user navigated away.
  useEffect(() => {
    const prev = prevSessionIdRef.current;
    if (prev !== null && sessionId !== null && sessionId !== prev) {
      suppressHydrationRef.current = false;
    }
    prevSessionIdRef.current = sessionId;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await hydrate(sessionId);
    })();
    return () => { cancelled = true; };
  }, [sessionId, hydrate]);

  // Hydrate on chat-channel events. The agent often publishes a centerPanel
  // RENDER right around the time it posts a chat MESSAGE; if the
  // centerPanel subscription missed the RENDER, the chat subscription almost
  // never misses the MESSAGE, so this acts as a self-healing fallback.
  const handleChatEvent = useCallback((event) => {
    if (!event) return;
    const { eventType } = event;
    if (eventType !== 'MESSAGE' && eventType !== 'STATE_CHANGE') return;
    // Fire-and-forget hydration. Throttled internally.
    hydrate(sessionId);
  }, [sessionId, hydrate]);

  useChannelSubscription(sessionId, 'chat', handleChatEvent);

  const clearPanel = useCallback(() => {
    setPanelConfig(null);
    setModalConfig(null);
  }, []);

  // Set panel config directly from a preloaded payload (e.g., the composite
  // session-context endpoint returns the persisted panel snapshot in the
  // same response as messages + chat list, so we don't have to fire a
  // separate getCenterPanel hydration request).
  //
  // Honors suppressHydrationRef the same way `hydrate` does — otherwise the
  // composite-fetch path on initial portal mount restores whatever the
  // agent last rendered for the auto-selected session (e.g. a topic
  // detail), overlaying it on whatever route the user actually landed on.
  // Suppression is lifted by a live RENDER (the agent intentionally
  // drawing on the current view) or by a real session switch (existing
  // logic in the sessionId effect above).
  const primePanel = useCallback((payload) => {
    if (!payload || !payload.layout || !Array.isArray(payload.components)) return;
    if (suppressHydrationRef.current) return;
    if (payload.layout === 'modal') {
      setModalConfig(payload);
    } else {
      setPanelConfig(payload);
    }
  }, []);
  const clearModal = useCallback(() => setModalConfig(null), []);
  // Show a panel because the USER asked for it (e.g. clicked a file chip in the
  // chat). Unlike primePanel this ignores hydration suppression: suppression keeps
  // stale agent snapshots off the page, not the user's own click.
  const showPanel = useCallback((payload) => {
    if (!payload || !payload.layout || !Array.isArray(payload.components)) return;
    setPanelConfig(payload);
  }, []);
  // Clears the panel AND blocks the next hydration cycle. Use this on user
  // navigation so the persisted server-side snapshot doesn't snap the panel
  // back when a chat event arrives. Cleared by a live RENDER on the new view
  // or a session change.
  const clearAndSuppressHydration = useCallback(() => {
    suppressHydrationRef.current = true;
    setPanelConfig(null);
    setModalConfig(null);
  }, []);

  return { panelConfig, modalConfig, clearPanel, clearModal, clearAndSuppressHydration, primePanel, showPanel };
}
